<script setup lang="ts">
import { required } from '@vuelidate/validators';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { useMeetingStore } from '@/store/meetingStore';
import { useProjectStore } from '@/store/projectStore';
import UiInput from '@/ui/form/UiInput.vue';
import UiCheckboxWithLabel from '@/ui/UiCheckboxWithLabel.vue';
import { useAlerts } from '@/utils/helpers/alerts';
interface Props {
  projectId: string;
  mode: 'start' | 'edit';
  meetingId?: string;
}

interface MeetingFormRequest {
  title: string;
  attendees: string[];
  other_attendees?: string;
  notes?: string;
  started_at?: string;
  ended_at?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'close'): void;
}>();

const projectStore = useProjectStore();
const meetingStore = useMeetingStore();
const message = useAlerts();
const { locale } = useI18n();

const project = computed(() => {
  if (projectStore.hasProject(props.projectId)) {
    return projectStore.project(props.projectId);
  }

  return null;
});

const projectMembers = computed(() =>
  projectStore.projectMembers(props.projectId),
);

const isLoading = ref(false);

const formData = ref<MeetingFormRequest>(
  props.mode === 'edit' ? blankUpdateFormData() : blankFormData(),
);

function blankFormData(): MeetingFormRequest {
  const today = DateTime.now();
  const titleFormat = locale.value === 'de' ? 'dd.MM.yyyy' : 'MM/dd/yyyy';
  const suggestedTitle = today.toFormat(titleFormat);

  return {
    title: suggestedTitle,
    attendees: [],
    other_attendees: '',
    notes: '',
  };
}

function blankUpdateFormData(): MeetingFormRequest {
  return {
    title: '',
    attendees: [],
    other_attendees: '',
    notes: '',
    started_at: '',
    ended_at: '',
  };
}

const rules = {
  title: [required],
  attendees: [required],
};

onMounted(async () => {
  if (props.mode === 'edit' && props.meetingId) {
    let meetingToEdit = meetingStore.meeting(props.projectId, props.meetingId);
    if (!meetingToEdit) {
      await meetingStore.loadAllMeetings(props.projectId);
      meetingToEdit = meetingStore.meeting(props.projectId, props.meetingId);
    }

    if (!meetingToEdit) {
      return;
    }

    formData.value.title = meetingToEdit.title;
    formData.value.attendees = meetingToEdit.attendees.map((a) => a.id);
    formData.value.other_attendees = meetingToEdit.other_attendees;
    formData.value.notes = meetingToEdit.notes;
    formData.value.started_at = meetingToEdit.started_at;
    formData.value.ended_at = meetingToEdit.ended_at;
  }
});

const toggleAttendee = (memberId: string) => {
  const memberIndex = formData.value.attendees.indexOf(memberId);

  if (memberIndex === -1) {
    formData.value.attendees.push(memberId);
  } else {
    formData.value.attendees.splice(memberIndex, 1);
  }
};

function startMeeting() {
  isLoading.value = true;
  meetingStore
    .startMeeting(props.projectId, formData.value)
    .then(() => {
      isLoading.value = false;
      message.success($t('projectMeetings.meetingStartedSuccessfully'));
      emit('close');
    })
    .catch((err) => {
      isLoading.value = false;
      message.error(err);
    });
}

const startedAt = computed<Date | null>({
  set(val) {
    if (!val) {
      formData.value.started_at = undefined;

      return;
    }
    const utcDate = DateTime.fromJSDate(val).toUTC().toISO();
    formData.value.started_at = utcDate;
  },
  get() {
    if (!formData.value.started_at) {
      return null;
    }
    const localDate = DateTime.fromISO(formData.value.started_at, {
      zone: 'utc',
    })
      .toLocal()
      .toJSDate();

    return localDate;
  },
});

const endedAt = computed<Date | null>({
  set(val) {
    if (!val) {
      formData.value.ended_at = undefined;

      return;
    }
    const utcDate = DateTime.fromJSDate(val).toUTC().toISO();
    formData.value.ended_at = utcDate;
  },
  get() {
    if (!formData.value.ended_at) {
      return null;
    }
    const localDate = DateTime.fromISO(formData.value.ended_at, { zone: 'utc' })
      .toLocal()
      .toJSDate();

    return localDate;
  },
});

function editMeeting() {
  if (!props.meetingId) {
    return;
  }
  isLoading.value = true;
  meetingStore
    .updateMeeting(props.projectId, props.meetingId, formData.value)
    .then(() => {
      message.success($t('projectMeetings.meetingUpdatedSuccessfully'));
      emit('close');
    })
    .catch((err) => {
      message.error(err);
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function save() {
  if (props.mode === 'start') {
    startMeeting();

    return;
  }
  editMeeting();
}
</script>

<template>
  <UiModal
    v-if="project"
    :title="$t('projectMeetings.meetingFor')"
    @close="emit('close')"
  >
    <template #header>
      <div class="modal-header">
        <div class="title">
          {{ $t('projectMeetings.meetingFor') }}
          <span v-if="project.title"> ({{ project.title }}) </span>
        </div>
      </div>
    </template>

    <UiForm :form-data="formData" :rules="rules" @form-submit="save">
      <template #default="{ formErrors }">
        <UiInput
          v-model="formData.title"
          :label="$t('project.title')"
          :errors="formErrors.title"
        />

        <UiField
          :label="$t('projectMeetings.attendees')"
          :errors="formErrors.attendees"
        >
          <div class="meeting-attendees-list">
            <UiCheckboxWithLabel
              v-for="member in projectMembers"
              :key="member.id"
              :value="formData.attendees.includes(member.id)"
              @toggle="toggleAttendee(member.id)"
            >
              {{ member.first_name }} {{ member.last_name }}
            </UiCheckboxWithLabel>
          </div>
        </UiField>

        <UiInput
          v-model="formData.other_attendees"
          :label="$t('projectMeetings.otherAttendees')"
          :placeholder="$t('projectMeetings.otherAttendeesPlaceholder')"
        />

        <div v-if="props.mode === 'edit'" class="date-pickers-row">
          <UiField :label="$t('projectMeetings.startedAt')">
            <UIDatePicker v-model="startedAt" :time="true" />
          </UiField>

          <UiField
            v-if="formData.ended_at"
            :label="$t('projectMeetings.endedAt')"
          >
            <UIDatePicker v-model="endedAt" :time="true" />
          </UiField>
        </div>

        <UiField :label="$t('projectMeetings.meetingNotes')">
          <AppWysiwygEditor
            v-model="formData.notes"
            tool-bar-position="bottom"
          />
        </UiField>
      </template>

      <template #bottom="{ formSubmit, hasErrors }">
        <UiButtonRow
          :cancel-button-text="$t('button.cancel')"
          :action-button-text="$t('button.confirm')"
          :loading="isLoading"
          :disabled="hasErrors"
          expand
          @cancel="emit('close')"
          @action="formSubmit"
        />
      </template>
    </UiForm>
  </UiModal>
</template>

<style lang="scss" scoped>
.modal-header {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  padding-bottom: var(--space-24);

  .title {
    display: flex;
    gap: var(--space-8);
    font-size: var(--font-size-20);
    line-height: var(--line-height-24);
    color: var(--color-gray-900);
    font-weight: var(--font-weight-medium);

    span {
      color: var(--color-gray-400);
    }
  }

  .subtitle {
    font-size: var(--font-size-14);
    color: var(--color-gray-500);
    font-weight: var(--font-weight-regular);
  }
}

.meeting-attendees-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 0.7rem 0;
}

.date-pickers-row {
  display: flex;
  gap: var(--space-16);
  .ui-field {
    flex: 1;
  }
}
</style>
